import React, { Suspense, useEffect } from 'react';
import { ErrorBoundary } from '@funtech-int/rival-react';
import { usePaymentPlace } from '@funtech-int/yango-shared';

import { useAutoOpenPaymentWidgetParams } from '../../hooks/useAutoOpenPaymentWidgetParams';
import { useLoginUrl } from '../../hooks/useLoginUrl';
import { getAutoOpenWidgetUrl } from '../../lib/getAutoOpenWidgetUrl';
import { useSubscriptionRefetch } from '../../hooks/useSubscriptionRefetch';
import { useOfferWebAnalytics } from '../../hooks/useOfferWebAnalytics';
import { usePaymentWidgetModal } from '../../hooks/usePopups';

interface WidgetOpenerProps {
    billingFeatureName: string | undefined;
    target: string;
    cleanOpenWidgetQuery: VoidFunction;
    onSuccessPurchase?: VoidFunction;
}

const WidgetOpener = ({
    billingFeatureName,
    target,
    cleanOpenWidgetQuery,
    onSuccessPurchase,
}: WidgetOpenerProps) => {
    const { refetch: subscriptionRefetch } = useSubscriptionRefetch();

    const paymentPlace = usePaymentPlace({
        target: target ?? '',
        features: [billingFeatureName ?? ''],
        extraParams: {
            customProperties: { billingFeatureName },
        },
    });

    const offer = paymentPlace?.offers?.[0] ?? null;

    useEffect(() => {
        const tariffOfferName = offer?.catalogCompositeOffer?.tariffOffer?.name;
        const optionOfferNames = offer?.catalogCompositeOffer?.optionOffers[0]?.name;

        const isEmptyOffer = !tariffOfferName && !optionOfferNames;

        if (isEmptyOffer) {
            cleanOpenWidgetQuery();
        }
    }, [cleanOpenWidgetQuery, offer]);

    const offerLoginUrl = useLoginUrl(getAutoOpenWidgetUrl(offer));
    const offerWebAnalytics = useOfferWebAnalytics(offer);

    const paymentWidget = usePaymentWidgetModal();

    useEffect(() => {
        function onSuccess() {
            subscriptionRefetch();
            offerWebAnalytics.purchaseSucceeded();
            onSuccessPurchase?.();
        }

        if (offer) {
            paymentWidget.open(paymentPlace, offer, {
                onClose: cleanOpenWidgetQuery,
                onPurchaseSuccess: onSuccess,
                onNeedAuth: () => window.location.assign(offerLoginUrl),
            });
        }
    }, [
        cleanOpenWidgetQuery,
        offer,
        offerLoginUrl,
        offerWebAnalytics,
        onSuccessPurchase,
        paymentWidget,
        paymentPlace,
        subscriptionRefetch,
    ]);

    return null;
};

interface AutoOpenPaymentWidgetProps {
    onSuccessPurchase?: VoidFunction;
}

export const AutoOpenPaymentWidget = ({ onSuccessPurchase }: AutoOpenPaymentWidgetProps) => {
    const { billingFeatureName, target, shouldOpenWidgetFromQuery, cleanOpenWidgetQuery } =
        useAutoOpenPaymentWidgetParams();

    if (shouldOpenWidgetFromQuery && target) {
        return (
            <ErrorBoundary fallback={() => null}>
                <Suspense fallback={null}>
                    <WidgetOpener
                        billingFeatureName={billingFeatureName}
                        target={target}
                        cleanOpenWidgetQuery={cleanOpenWidgetQuery}
                        onSuccessPurchase={onSuccessPurchase}
                    />
                </Suspense>
            </ErrorBoundary>
        );
    }
};
